import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  TrendingUp, TrendingDown, ChevronDown, LifeBuoy, LogOut, Settings,
  ArrowRight, Loader2, CalendarDays,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../utils/api';

const RANGES = [
  { id: 'last7', label: 'Last 7 days', days: 7 },
  { id: 'last30', label: 'Last 30 days', days: 30 },
  { id: 'last90', label: 'Last 90 days', days: 90 },
  { id: 'month', label: 'This month', period: 'month' },
  { id: 'year', label: 'This year', period: 'year' },
  { id: 'all', label: 'All time', period: 'all' },
];

const PIE_COLORS = ['#2563eb', '#10b981', '#8b5cf6', '#f59e0b', '#94a3b8'];

const CHANNEL_RULES = [
  [/kiosk|walk/i, 'Walk-in'],
  [/getmyboat|gmb/i, 'GetMyBoat'],
  [/getyourguide|gyg/i, 'GetYourGuide'],
  [/admin|internal|manual|phone/i, 'Manual'],
  [/widget|website|online|direct|flow/i, 'Website'],
];
const channelLabel = (src) => {
  for (const [re, label] of CHANNEL_RULES) if (re.test(src || '')) return label;
  return 'Other';
};

const fetcher = (url) => api.get(url).then((r) => r.data);

const rangeQuery = (range) => {
  if (range.period) return `period=${range.period}`;
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (range.days - 1));
  const fmt = (d) => d.toISOString().slice(0, 10);
  return `period=custom&startDate=${fmt(start)}&endDate=${fmt(end)}`;
};

const compact = (sym, v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `${sym}${(n / 1000).toFixed(1)}k`;
  return `${sym}${n.toFixed(0)}`;
};

const TrendChip = ({ value }) => {
  if (value === null || value === undefined || value === 0) return null;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{Number(value).toFixed(1)}%
    </span>
  );
};

const KpiCard = ({ label, value, trend, testid }) => (
  <div className="card-soft p-4 sm:p-5" data-testid={testid}>
    <p className="text-[11px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1.5 truncate">{value}</p>
    <div className="mt-1 h-4"><TrendChip value={trend} /></div>
  </div>
);

const Card = ({ title, action, onAction, children, testid }) => (
  <div className="card-soft p-4 sm:p-5 flex flex-col" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm sm:text-[15px] font-semibold text-gray-900">{title}</h3>
      {action && (
        <button onClick={onAction} className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid={`${testid}-action`}>
          {action} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    {children}
  </div>
);

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const bookingTimeLabel = (b) => {
  if (!b.date) return '';
  const today = new Date().toISOString().slice(0, 10);
  const d = new Date(`${b.date}T00:00:00`);
  const day = b.date === today ? 'Today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!b.time) return day;
  const [h, m] = b.time.split(':');
  const hour = parseInt(h, 10);
  return `${day}, ${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

export default function DashboardHomeTab({ onNavigate, onLogout, onOpenHelp }) {
  const [range, setRange] = useState(RANGES[1]); // Last 30 days
  const userData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); } catch { return {}; }
  }, []);

  const { data: stats, isLoading } = useSWR(
    `/v2/dashboard/stats?${rangeQuery(range)}`,
    fetcher,
    { revalidateOnFocus: false }
  );

  const sym = stats?.currencySymbol || '$';

  const revenueSeries = useMemo(() => (stats?.chartData || []).map((d) => ({
    label: d.label,
    revenue: Math.round(((d.completed || 0) + (d.projected || 0)) * 100) / 100,
  })), [stats]);

  const channels = useMemo(() => {
    const agg = {};
    Object.entries(stats?.bookings?.sources || {}).forEach(([src, count]) => {
      const label = channelLabel(src);
      agg[label] = (agg[label] || 0) + count;
    });
    return Object.entries(agg).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [stats]);

  const topServices = stats?.topServices || [];
  const donutTotal = topServices.reduce((s, x) => s + (x.revenue || 0), 0);
  const money = (v, digits = 0) => `${sym}${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  const conversionRate = stats?.conversion?.rate;

  if (isLoading && !stats) {
    return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-5 mt-2" data-testid="dashboard-home">
      {/* Header — title left, range + profile top right */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5 hidden sm:block">Your business at a glance</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50" data-testid="dash-range-select">
                <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                {range.label}
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {RANGES.map((r) => (
                <DropdownMenuItem key={r.id} onClick={() => setRange(r)} className={r.id === range.id ? 'font-semibold text-blue-600' : ''} data-testid={`dash-range-${r.id}`}>
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard label="Total Bookings" value={(stats?.bookings?.total ?? 0).toLocaleString()} trend={stats?.bookingsTrend} testid="dash-kpi-bookings" />
        <KpiCard label="Revenue" value={money(stats?.revenue?.total)} trend={stats?.revenue?.trend} testid="dash-kpi-revenue" />
        <KpiCard label="Avg. Order Value" value={money(stats?.avgBookingValue, 2)} trend={stats?.aovTrend} testid="dash-kpi-aov" />
        <KpiCard label="Conversion Rate" value={conversionRate === null || conversionRate === undefined ? '—' : `${Number(conversionRate).toFixed(1)}%`} trend={stats?.conversion?.trend} testid="dash-kpi-conversion" />
      </div>

      {/* Bookings revenue chart + Top services donut */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="lg:col-span-3">
          <Card title="Bookings" action="View full report" onAction={() => onNavigate?.('revenue')} testid="dash-revenue-chart">
            <div className="h-56 sm:h-64 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dashRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => compact(sym, v)} />
                  <Tooltip formatter={(v) => [money(v, 2), 'Revenue']} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 13 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} fill="url(#dashRev)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card title="Top Services" action="View full report" onAction={() => onNavigate?.('reports')} testid="dash-top-services">
            {topServices.length === 0 ? (
              <p className="text-sm text-gray-400 py-10 text-center">No paid bookings in this range yet.</p>
            ) : (
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-center gap-4">
                <div className="relative w-40 h-40 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={topServices} dataKey="revenue" nameKey="name" innerRadius={52} outerRadius={74} paddingAngle={2} strokeWidth={0}>
                        {topServices.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [money(v, 2), n]} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 13 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-lg font-bold text-gray-900">{compact(sym, donutTotal)}</span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">Total</span>
                  </div>
                </div>
                <ul className="flex-1 w-full space-y-2 min-w-0">
                  {topServices.map((s, i) => (
                    <li key={s.name} className="flex items-center gap-2 text-sm">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-gray-600 truncate flex-1">{s.name}</span>
                      <span className="font-semibold text-gray-900 flex-shrink-0">{compact(sym, s.revenue)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Recent bookings + channel bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="lg:col-span-3">
          <Card title="Recent Bookings" action="View all bookings" onAction={() => onNavigate?.('schedule')} testid="dash-recent-bookings">
            {(stats?.recentBookings || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-10 text-center">No bookings yet — they'll appear here the moment one lands.</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {(stats?.recentBookings || []).slice(0, 5).map((b) => (
                  <li key={b.id} className="flex items-center gap-3 py-2.5" data-testid={`dash-recent-row-${b.id}`}>
                    <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {initials(b.customerName)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{b.customerName || 'Guest'}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {b.serviceName || 'Booking'}{b.partySize ? ` · ${b.partySize} guest${b.partySize > 1 ? 's' : ''}` : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">{money(b.totalAmount, 2)}</p>
                      <p className="text-[11px] text-gray-400">{bookingTimeLabel(b)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card title="Bookings by Channel" testid="dash-channel-chart">
            {channels.length === 0 ? (
              <p className="text-sm text-gray-400 py-10 text-center">No bookings in this range yet.</p>
            ) : (
              <div className="h-56 sm:h-64 -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channels} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, 'Bookings']} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 13 }} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={38} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
